'use client';

import { Sample, Group } from '@/sharedTypes/Api';
import React, { useEffect, useState, useCallback, useRef } from 'react';
import { apiSwaggerClientSide } from '@/shared/api';
import { useLanguageFromPath } from '@/hooks/useLanguageFromPath';
import { Checkbox } from '@/components/ui/checkbox';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { CatalogueFilters } from '../views/CatalogueTableView';
import { ButtonShadcn } from '@/components/ui/button-shadcn';
import { ChevronLeft, ChevronRight, ChevronUp, ChevronDown, ChevronsUpDown, Upload } from 'lucide-react';
import { AdminCatalogueView, SelectedSample } from '../Catalogue';
import toast from '@/components/ui/custom-toast';
import { SortDirection, SortState } from '../views/CatalogueTableView';
import { useTranslation } from 'react-i18next';

const PAGE_SIZE = 20;

// Sort chevron component with visual indicator
const SortChevron = ({
  direction,
  onClick,
  t
}: {
  direction: SortDirection;
  onClick: () => void;
  t: (key: string) => string;
}) => {
  const labels: Record<SortDirection, string> = {
    none: '',
    asc: 'A-Z',
    desc: 'Z-A',
  };

  return (
    <button
      onClick={onClick}
      className="inline-flex items-center gap-1 ml-1 p-0.5 rounded hover:bg-gray-200 transition-colors cursor-pointer"
      title={direction === 'none' ? t('adminCatalogue.catalogueTable.clickToSortAZ') : direction === 'asc' ? t('adminCatalogue.catalogueTable.clickToSortZA') : t('adminCatalogue.catalogueTable.clickToRemoveSort')}
    >
      {direction === 'none' && <ChevronsUpDown className="h-4 w-4 text-gray-400" />}
      {direction === 'asc' && <ChevronUp className="h-4 w-4 text-blue-600" />}
      {direction === 'desc' && <ChevronDown className="h-4 w-4 text-blue-600" />}
      {direction !== 'none' && (
        <span className="text-xs font-medium text-blue-600">{labels[direction]}</span>
      )}
    </button>
  );
};

interface CatalogueTableProps {
  filters: CatalogueFilters;
  page: number;
  onPageChange: (page: number) => void;
  selectedSamples: SelectedSample[];
  onSelectionChange: (samples: SelectedSample[]) => void;
  setView: React.Dispatch<React.SetStateAction<AdminCatalogueView>>;
  sort: SortState;
  onSortChange: (column: 'title' | 'author') => void;
  onCsvReview: (data: { matched: SelectedSample[]; unmatched: string[] }) => void;
}

interface PaginationData {
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
}
// ** AI POINT THIS OUT: NEED TO ADD CACHE IN ORDER TO NOT ALWAYS MAKE FRESH REQUESTS
// Need to add tests on backend for samplesResolveCreate to ensure it correctly matches identifiers and handles edge cases (empty, all invalid, mix of valid/invalid, etc)
const CatalogueTable = ({
  filters,
  page,
  onPageChange,
  selectedSamples,
  onSelectionChange,
  setView,
  sort,
  onSortChange,
  onCsvReview
}: CatalogueTableProps) => {
  const { t } = useTranslation();
  const language = useLanguageFromPath();
  const otherLanguage = language === 'en' ? 'ru' : 'en'

  const [samples, setSamples] = useState<Sample[]>([]);
  const [pagination, setPagination] = useState<PaginationData | null>(null);
  const [loading, setLoading] = useState(true);
  const [csvLoading, setCsvLoading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const MAX_IDENTIFIERS = 500;
  const IDENTIFIER_LENGTH = 12;

  const parseCsv = (text: string): { identifiers: string[]; skipped: number } => {
    // Remove BOM if present
    const cleaned = text.replace(/^\uFEFF/, '');
    const lines = cleaned.split(/\r?\n/).filter((line) => line.trim().length > 0);
    if (lines.length === 0) return { identifiers: [], skipped: 0 };

    const firstRowCols = lines[0].split(',').map((c) => c.trim().replace(/^"|"$/g, ''));
    const hasMultipleColumns = firstRowCols.length > 1;

    // Detect identifier column index from header row
    let colIndex = 0;
    let dataStartRow = 0;

    if (hasMultipleColumns) {
      const identifierColIndex = firstRowCols.findIndex(
        (col) => col.toLowerCase() === 'identifier'
      );
      if (identifierColIndex !== -1) {
        colIndex = identifierColIndex;
        dataStartRow = 1; // skip header
      }
    }

    // Strip surrounding quotes from CSV values
    const stripQuotes = (val: string): string => {
      const trimmed = val.trim();
      if (trimmed.length >= 2 && trimmed[0] === '"' && trimmed[trimmed.length - 1] === '"') {
        return trimmed.slice(1, -1);
      }
      return trimmed;
    };

    // Extract values from the identified column
    const rawValues = lines.slice(dataStartRow).map(
      (line) => stripQuotes(line.split(',')[colIndex] ?? '')
    );

    // Filter to valid 12-char identifiers
    let skipped = 0;
    const identifiers = rawValues.filter((val) => {
      if (val.length === IDENTIFIER_LENGTH) return true;
      if (val.length > 0) skipped++;
      return false;
    });

    // Cap at max
    return {
      identifiers: identifiers.slice(0, MAX_IDENTIFIERS),
      skipped: skipped + Math.max(0, identifiers.length - MAX_IDENTIFIERS),
    };
  };

  const handleCsvUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Reset input so same file can be re-uploaded
    e.target.value = '';

    setCsvLoading(true);
    try {
      const text = await file.text();
      const { identifiers, skipped } = parseCsv(text);

      if (identifiers.length === 0) {
        toast({
          title: t('adminCatalogue.csvUpload.error'),
          description: skipped > 0
            ? t('adminCatalogue.csvUpload.invalidFormat', { count: skipped })
            : t('adminCatalogue.csvUpload.emptyFile'),
          type: 'error',
        });
        return;
      }

      const response = await apiSwaggerClientSide.admin.samplesResolveCreate({ identifiers });

      if (!response.data?.success || !response.data.data) {
        toast({ title: t('adminCatalogue.csvUpload.error'), description: t('adminCatalogue.csvUpload.error'), type: 'error' });
        return;
      }

      const { matched, unmatched } = response.data.data;

      if (!matched || matched.length === 0) {
        toast({ title: t('adminCatalogue.csvUpload.error'), description: t('adminCatalogue.csvUpload.noMatches'), type: 'error' });
        return;
      }

      // Convert matched samples to SelectedSample[]
      const selectedFromCsv: SelectedSample[] = matched.map((sample) => ({
        id: sample._id,
        sample: {
          title: sample.metadata?.title?.[language] || sample.metadata?.title?.[otherLanguage] || sample.identifier || '',
          accessControl: {
            allowedGroups: (sample.accessControl?.allowedGroups || []).map((g) =>
              typeof g === 'string' ? { _id: g, name: g } : { _id: (g as Group)._id || '', name: (g as Group).name || '' }
            ),
            allowedUsers: (sample.accessControl?.allowedUsers || []).map((u) =>
              typeof u === 'string' ? { sub: u, username: u } : { sub: u.sub || '', username: u.username || '' }
            ),
          },
          isPrivate: sample.isPrivate || false,
        },
      }));

      onCsvReview({ matched: selectedFromCsv, unmatched: unmatched || [] });
    } catch (error) {
      console.error('CSV upload failed:', error);
      toast({ title: t('adminCatalogue.csvUpload.error'), description: t('adminCatalogue.csvUpload.error'), type: 'error' });
    } finally {
      setCsvLoading(false);
    }
  };

  const fetchSamples = useCallback(async () => {
    setLoading(true);
    try {
      const query: Record<string, string | number> = {
        page,
        pageSize: PAGE_SIZE,
      };

      if (filters.search) {
        query.search = filters.search;
      }
      if (filters.searchAuthor) {
        query.searchAuthor = filters.searchAuthor;
      }
      if (filters.visibility && filters.visibility !== 'all') {
        query.visibility = filters.visibility;
      }
      if (filters.group && filters.group !== 'all') {
        query.group = filters.group;
      }

      const response = await apiSwaggerClientSide.admin.samplesList(
        query as Parameters<typeof apiSwaggerClientSide.admin.samplesList>[0]
      );

      if (response.data?.success && response.data.data) {
        setSamples(response.data.data.samples || []);
        setPagination(response.data.data.pagination as PaginationData || null);
      }
    } catch (error) {
      console.error('Failed to fetch samples:', error);
    } finally {
      setLoading(false);
    }
  }, [filters, page]);

  useEffect(() => {
    const debounce = setTimeout(() => {
      fetchSamples();
    }, 300);
    return () => clearTimeout(debounce);
  }, [fetchSamples]);

  useEffect(() => {
    if (!loading) {
      document.dispatchEvent(new CustomEvent('catalogueTableLoaded'));
    }
  }, [loading]);

  const mapSampleToSelected = (sample: Sample): SelectedSample => ({
    id: sample._id,
    sample: {
      title: sample.metadata?.title?.[language] || sample.metadata?.title?.[otherLanguage] || sample.identifier,
      accessControl: {
        allowedGroups: (sample.accessControl?.allowedGroups || []).map((g) =>
          typeof g === 'string' ? { _id: g, name: g } : { _id: (g as Group)._id || '', name: (g as Group).name || '' }
        ),
        allowedUsers: (sample.accessControl?.allowedUsers || []).map((u) =>
          typeof u === 'string' ? { sub: u, username: u } : { sub: u.sub || '', username: u.username || '' }
        ),
      },
      isPrivate: sample.isPrivate || false
    },
  });

  // Apply frontend sorting to samples
  const getSampleTitle = (sample: Sample): string => {
    return (sample.metadata?.title?.[language] || sample.metadata?.title?.[otherLanguage] || sample.identifier || '');
  };

  const getSampleAuthor = (sample: Sample): string => {
    return (sample.metadata?.author?.[language] || sample.metadata?.author?.[otherLanguage] || '');
  };

  // Robust string comparison that handles:
  // - Numbers as prefixes ("2 Sonata" before "10 Sonata")
  // - Russian/Cyrillic characters
  // - Mixed scripts (Latin + Cyrillic)
  // - Symbols and special characters (sorted to end)
  // - Empty strings (sorted to end)
  const compareStrings = (a: string, b: string, direction: 'asc' | 'desc'): number => {
    // Handle empty strings - push to end
    if (!a && !b) return 0;
    if (!a) return 1;
    if (!b) return -1;

    // Use Intl.Collator for proper locale-aware comparison
    // undefined locale = use browser's default, handles both en and ru
    const collator = new Intl.Collator(undefined, {
      numeric: true,        // "2" before "10"
      sensitivity: 'base',  // case-insensitive, accent-insensitive
      ignorePunctuation: true, // ignore punctuation differences
    });

    const result = collator.compare(a, b);
    return direction === 'asc' ? result : -result;
  };

  const sortedSamples = React.useMemo(() => {
    if (sort.titleSort === 'none' && sort.authorSort === 'none') {
      return samples;
    }

    const sorted = [...samples];

    if (sort.titleSort !== 'none') {
      sorted.sort((a, b) => compareStrings(getSampleTitle(a), getSampleTitle(b), sort.titleSort as 'asc' | 'desc'));
    } else if (sort.authorSort !== 'none') {
      sorted.sort((a, b) => compareStrings(getSampleAuthor(a), getSampleAuthor(b), sort.authorSort as 'asc' | 'desc'));
    }

    return sorted;
  }, [samples, sort, language, otherLanguage]);

  const selectedIds = selectedSamples.map((s) => s.id);
  const currentPageIds = sortedSamples.map((s) => s._id);

  // Check how many samples on CURRENT PAGE are selected
  const selectedOnCurrentPage = currentPageIds.filter((id) => selectedIds.includes(id));
  const allSelectedOnPage = sortedSamples.length > 0 && selectedOnCurrentPage.length === sortedSamples.length;
  const someSelectedOnPage = selectedOnCurrentPage.length > 0 && selectedOnCurrentPage.length < sortedSamples.length;

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      // Add current page samples to existing selections (avoid duplicates)
      const newSelections = sortedSamples
        .filter((s) => !selectedIds.includes(s._id))
        .map(mapSampleToSelected);
      onSelectionChange([...selectedSamples, ...newSelections]);
    } else {
      // Remove only current page samples, keep selections from other pages
      onSelectionChange(selectedSamples.filter((s) => !currentPageIds.includes(s.id)));
    }
  };

  const handleSelectOne = (sample: Sample, checked: boolean) => {
    if (checked) {
      onSelectionChange([...selectedSamples, mapSampleToSelected(sample)]);
    } else {
      onSelectionChange(selectedSamples.filter((s) => s.id !== sample._id));
    }
  };

  const getAccessDisplay = (sample: Sample) => {
    const isHidden = sample.isPrivate

    if(isHidden) {
      return (
        <span data-testid={`catalogue-access-hidden-${sample._id}`} className="px-2 py-1 text-xs font-medium rounded bg-gray-600 text-gray-800">
          {t('adminCatalogue.access.hidden')}
        </span>
      );
    }

  const isRestricted = (sample.accessControl?.allowedGroups?.length ?? 0) > 0 || (sample.accessControl?.allowedUsers?.length ?? 0) > 0

    if (isRestricted) {
      const allowedGroups = sample.accessControl?.allowedGroups || [];
      const allowedUsers = sample.accessControl?.allowedUsers || [];

      const tooltipContent = (
        <div className="text-xs">
          {allowedGroups.length > 0 && (
            <div>
              <span className="font-semibold">{t('adminCatalogue.accessControl.groups')}:</span>{' '}
              {allowedGroups
                .map((g) => (typeof g === 'string' ? g : (g as Group).name))
                .join(', ')}
            </div>
          )}
          {allowedUsers.length > 0 && (
            <div>
              <span className="font-semibold">{t('adminCatalogue.accessControl.users')}:</span>{' '}
              {allowedUsers
                .map((u) => (typeof u === 'string' ? u : u.username))
                .join(', ')}
            </div>
          )}
          {allowedGroups.length === 0 && allowedUsers.length === 0 && (
            <div>No specific access configured</div>
          )}
        </div>
      );

      return (
        <Tooltip>
          <TooltipTrigger asChild>
            <span data-testid={`catalogue-access-restricted-${sample._id}`} className="px-2 py-1 text-xs font-medium rounded bg-red-100 text-red-800 cursor-help">
              {t('adminCatalogue.access.restricted')}
            </span>
          </TooltipTrigger>
          <TooltipContent side="left">{tooltipContent}</TooltipContent>
        </Tooltip>
      );
    }

    return (
      <span data-testid={`catalogue-access-public-${sample._id}`} className="px-2 py-1 text-xs font-medium rounded bg-green-100 text-green-800">
        {t('adminCatalogue.access.public')}
      </span>
    );
  };

  if (loading) {
    return (
      <div data-testid="catalogue-table-loading" className="flex flex-col h-full">
        <div className="border rounded-lg overflow-hidden flex-1 min-h-0 overflow-y-auto">
          <table className="w-full">
            <thead className="bg-gray-50 sticky top-0 z-10">
              <tr>
              <th className="w-12 px-4 py-3"><Skeleton className="h-4 w-4" /></th>
              <th className="px-4 py-3 text-left"><Skeleton className="h-4 w-20" /></th>
              <th className="px-4 py-3 text-left"><Skeleton className="h-4 w-16" /></th>
              <th className="px-4 py-3 text-left"><Skeleton className="h-4 w-16" /></th>
            </tr>
          </thead>
          <tbody>
            {Array.from({ length: 10 }).map((_, i) => (
              <tr key={i} className="border-t">
                <td className="px-4 py-3"><Skeleton className="h-4 w-4" /></td>
                <td className="px-4 py-3"><Skeleton className="h-4 w-48" /></td>
                <td className="px-4 py-3"><Skeleton className="h-4 w-32" /></td>
                <td className="px-4 py-3"><Skeleton className="h-6 w-16" /></td>
              </tr>
            ))}
          </tbody>
          </table>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="catalogue-table-loaded" className="flex flex-col h-full">
      <input
        ref={fileInputRef}
        data-testid="catalogue-csv-file-input"
        type="file"
        accept=".csv"
        className="hidden"
        onChange={handleCsvUpload}
      />
      <div className="border rounded-lg overflow-hidden flex-1 min-h-0 overflow-y-auto">
        <table className="w-full">
          <thead className="bg-gray-50 sticky top-0 z-10">
            <tr>
              <th data-testid="catalogue-header-checkbox" className="w-12 px-4 py-3">
                <Checkbox
                  checked={allSelectedOnPage}
                  ref={(el) => {
                    if (el) (el as HTMLButtonElement).dataset.state = someSelectedOnPage ? 'indeterminate' : allSelectedOnPage ? 'checked' : 'unchecked';
                  }}
                  onCheckedChange={handleSelectAll}
                />
              </th>
              <th data-testid="catalogue-header-title" className="px-4 py-3 text-left text-sm font-medium text-gray-700">
                <span className="inline-flex items-center">
                  {t('adminCatalogue.catalogueTable.title')}
                  <SortChevron direction={sort.titleSort} onClick={() => onSortChange('title')} t={t} />
                </span>
              </th>
              <th data-testid="catalogue-header-author" className="px-4 py-3 text-left text-sm font-medium text-gray-700">
                <span className="inline-flex items-center">
                  {t('adminCatalogue.catalogueTable.author')}
                  <SortChevron direction={sort.authorSort} onClick={() => onSortChange('author')} t={t} />
                </span>
              </th>
              <th data-testid="catalogue-header-access" className="px-4 py-3 text-left text-sm font-medium text-gray-700">
                {t('adminCatalogue.catalogueTable.access')}
              </th>
            </tr>
          </thead>
          <tbody>
            {sortedSamples.length === 0 ? (
              <tr>
                <td data-testid="catalogue-empty" colSpan={4} className="px-4 py-8 text-center text-gray-500">
                  {t('adminCatalogue.catalogueTable.noMusicSheetsFound')}
                </td>
              </tr>
            ) : (
              sortedSamples.map((sample) => {
                return (
                  <tr
                    key={sample._id}
                    data-testid={`catalogue-row-${sample._id}`}
                    className="border-t hover:bg-gray-50 transition-colors"
                  >
                    <td className="px-4 py-3">
                      <Checkbox
                        data-testid={`catalogue-checkbox-${sample._id}`}
                        checked={selectedIds.includes(sample._id)}
                        onCheckedChange={(checked) =>
                          handleSelectOne(sample, checked as boolean)
                        }
                      />
                    </td>
                    <td data-testid={`catalogue-title-${sample._id}`} className="px-4 py-3 text-sm">
                      {sample.metadata?.title?.[language] ||
                        sample.metadata?.title?.[otherLanguage] ||
                        sample.identifier}
                    </td>
                  <td data-testid={`catalogue-author-${sample._id}`} className="px-4 py-3 text-sm text-gray-600">
                    {sample.metadata?.author?.[language] ||
                      sample.metadata?.author?.[otherLanguage] ||
                      '-'}
                  </td>
                  <td data-testid={`catalogue-access-${sample._id}`} className="px-4 py-3">{getAccessDisplay(sample)}</td>
                </tr>
              );
            })
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {pagination && pagination.totalPages > 1 ? (
        <div className="flex items-center justify-between my-4 px-2">
          <div data-testid="catalogue-pagination-summary" className="text-sm text-gray-600">
            {t('adminCatalogue.common.showing', { from: (page - 1) * PAGE_SIZE + 1, to: Math.min(page * PAGE_SIZE, pagination.total), total: pagination.total })}
          </div>
          <div className="flex items-center gap-2">
            <ButtonShadcn
              data-testid="catalogue-csv-upload-btn"
              variant="outline"
              size="sm"
              onClick={() => fileInputRef.current?.click()}
              disabled={csvLoading}
              className="cursor-pointer"
            >
              <Upload className="h-4 w-4 mr-1" />
              {csvLoading ? t('adminCatalogue.common.loading') : t('adminCatalogue.csvUpload.uploadCsv')}
            </ButtonShadcn>
            <ButtonShadcn
              data-testid="catalogue-manage-access-btn"
              variant="default"
              className={`${selectedSamples.length === 0 ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
              size="sm"
              onClick={() => setView('ACCESS_CONTROL')}
              disabled={selectedSamples.length === 0}
            >
              {t('adminCatalogue.catalogueTable.manageAccess')} {selectedSamples.length>0 ? `: ${selectedSamples.length} ${t('adminCatalogue.catalogueTable.selected')}` : ''}
            </ButtonShadcn>
            <ButtonShadcn
              data-testid="catalogue-pagination-prev"
              variant="outline"
              size="sm"
              onClick={() => onPageChange(page - 1)}
              disabled={page <= 1}
            >
              <ChevronLeft className="h-4 w-4" />
              {t('adminCatalogue.common.previous')}
            </ButtonShadcn>
            <span data-testid="catalogue-pagination-info" className="text-sm text-gray-600">
              {t('adminCatalogue.common.pageOf', { page, total: pagination.totalPages })}
            </span>
            <ButtonShadcn
              data-testid="catalogue-pagination-next"
              variant="outline"
              size="sm"
              onClick={() => onPageChange(page + 1)}
              disabled={page >= pagination.totalPages}
            >
              {t('adminCatalogue.common.next')}
              <ChevronRight className="h-4 w-4" />
            </ButtonShadcn>
          </div>
        </div>
      ) : (
      <div className="flex items-center gap-2 mt-2">
        <ButtonShadcn
          data-testid="catalogue-csv-upload-btn-single"
          variant="outline"
          size="sm"
          onClick={() => fileInputRef.current?.click()}
          disabled={csvLoading}
          className="cursor-pointer"
        >
          <Upload className="h-4 w-4 mr-1" />
          {csvLoading ? t('adminCatalogue.common.loading') : t('adminCatalogue.csvUpload.uploadCsv')}
        </ButtonShadcn>
        <ButtonShadcn
          data-testid="catalogue-manage-access-btn"
          variant="default"
          className={`${selectedSamples.length === 0 ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
          size="sm"
          onClick={() => setView('ACCESS_CONTROL')}
          disabled={selectedSamples.length === 0}
        >
          {t('adminCatalogue.catalogueTable.manageAccess')} {selectedSamples.length>0 ? `: ${selectedSamples.length} ${t('adminCatalogue.catalogueTable.selected')}` : ''}
        </ButtonShadcn>
      </div>
      )}

      {/* Manage Access */}
      {/* {(someSelectedOnPage || allSelectedOnPage) ? (
        <button
        className='cursor-pointer border-0 bg-transparent p-0 w-fit text-sm px-2'
        onClick={() => setView('ACCESS_CONTROL')}
        >
          Manage Access: {selectedSamples.length} selected
        </button>
      ) : (
        <div className='px-2 flex gap-2 items-center text-sm'>
            <p>Bulk Action Bar: 0 items selected</p>
            <p className='text-gray-500'>(Changes to "Manage Access" on click)</p>
        </div>
      )} */}
    </div>
  );
};

export default CatalogueTable;
